import React, { useState, useEffect } from "react";
import styles from "./Grievances.module.css";
import { grievanceService } from "../../services/grievanceService";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { REACT_APP_API_BASE_URL } from "../../apiurl";
import CollapsibleTable from "../../components/common/CollapsibleTable/CollapsibleTable";
import { usePermission } from "../../hooks/usePermissions";
import { formatISTDate, formatISTDateTime, formatISTDateTimeFromParts } from '../../utils/datetime';
import Modal from "../../components/common/Modal/Modal";
import AddGrievances from "./AddGrievances";
import AddGrievanceResponse from "../../components/grievances/GrievanceResponse/AddGrievanceResponse";
import LeadNotesModal from "./LeadNotesModal";

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export default function Grievances() {
  const navigate = useNavigate();

  const [grievances, setGrievances] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [complaintTypeOptions, setComplaintTypeOptions] = useState([]);
  const [complaintType, setComplaintType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedData, setExpandedData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedComplaintId, setSelectedComplaintId] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState(null);

  const { hasPermission } = usePermission()

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({
    status: "",
    complaint_type: "",
    from: "",
    to: "",
    search: "",
  });

  // Fetch data
  const fetchGrievances = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await grievanceService.getAllGrievance({ ...filters, page, limit: 20 });
      setGrievances(response?.data || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      toast.error("❌ Failed to load Ticket!");
      setGrievances([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const response = await grievanceService.getStatusOptions();
      setStatusOptions(response || []);
    } catch (error) {
      toast.error("❌ Failed to load status options!");
    }
  };

  const fetchComplaintTypes = async () => {
    try {
      const response = await grievanceService.getComplaintTypesOptions();
      setComplaintTypeOptions(response || []);
    } catch (error) {
      toast.error("❌ Failed to load complaint type options!");
    }
  };

  useEffect(() => {
    fetchStatusOptions();
    fetchComplaintTypes();
  }, []);

  useEffect(() => {
    fetchGrievances({
      ...appliedFilters,
      page,
      limit: 20,
    });
  }, [page, appliedFilters]);

  const handleExpand = async (row) => {
    const id = row.complaint_id;

    if (expandedRow === id) {
      setExpandedRow(null);
      setExpandedData([]);
      return;
    }

    try {
      const response = await fetch(
        `${REACT_APP_API_BASE_URL}api/grievance/grievancemeta/${id}`
      );
      const result = await response.json();

      setExpandedRow(id);
      setExpandedData(result.data || []);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load details");
    }
  };

  const tableData = grievances.map((g) => ({
    ...g,
    _expanded: expandedRow === g.complaint_id,
    childData: expandedRow === g.complaint_id ? expandedData : [],
  }));

  const getRowStyle = (row) => ({
    background: "white",
  });

  // Table Columns
  const columns = [
    {
      key: "expand_action",
      label: "",
      width: "40px",
      render: (_, row) => (
        <button
          onClick={() => handleExpand(row)}
          className={styles.expandButton}
          title="Expand"
        >
          {expandedRow === row.complaint_id ? "-" : "+"}
        </button>
      ),
    },
    { key: "complaint_id", label: "COMPLAINT ID" },
    { key: "complaint_type", label: "COMPLAINT TYPE" },
    { key: "issue_type", label: "ISSUE TYPE" },
    { key: "userid", label: "USER ID" },
    { key: "name", label: "NAME" },
    { key: "phone", label: "PHONE" },
    { key: "email", label: "EMAIL" },
    { key: "subject", label: "SUBJECT" },
    { key: "message_text", label: "MESSAGE" },
    {
      key: "date_time",
      label: "DATE & TIME",
      render: (_, row) => {
        return formatISTDateTimeFromParts(row.submitted_date, row.submitted_time);
      },
    },
    { key: "complaint_status", label: "STATUS" },
    {
      key: "attachment",
      label: "ATTACHMENT",
      render: (attachments) => {
        if (!attachments?.length) return "No File";

        const downloadFile = async (file) => {
          const cleanPath = file.path.split("victoryBackend/")[1];
          const url = `${REACT_APP_API_BASE_URL}${cleanPath}`;
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = file.filename || "download";
            a.click();
            window.URL.revokeObjectURL(blobUrl);
          } catch (err) {
            console.error("Download failed", err);
          }
        };

        return attachments.map((file, i) => (
          <span
            key={i}
            onClick={() => downloadFile(file)}
            className={styles.downloadIconWrapper}
            title="Download"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 3v12m0 0l-5-5m5 5l5-5M5 19h14" stroke="#000" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ));
      },
    },
    {
      key: "lead_notes",
      label: "NOTES",
      // lead_notes is only populated for tickets raised by the investment
      // apply/lead-capture flow (IPO, NCD, Corporate Bond, Fixed Deposit) —
      // every other ticket type simply shows nothing in this column.
      render: (notes) =>
        notes ? (
          <button
            className={styles.notesButton}
            onClick={() => {
              setSelectedNotes(notes);
              setShowNotesModal(true);
            }}
          >
            View
          </button>
        ) : (
          "—"
        ),
    },
    {
      key: "actions",
      label: "ACTIONS",
      render: (_, row) => (
        <button
          className={styles.actionButton}
          onClick={() => {
            setSelectedComplaintId(row.complaint_id);
            setShowResponseModal(true);
          }}
        >
          Response
        </button>
      ),
    },
  ];

  const handleApplyFilters = () => {
    const newFilters = {
      status: statusFilter !== "All" ? statusFilter : "",
      complaint_type: complaintType !== "All" ? complaintType : "",
      from: fromDate,
      to: toDate,
      search: searchTerm,
    };
    if (JSON.stringify(newFilters) !== JSON.stringify(appliedFilters)) {
      setAppliedFilters(newFilters);
      setPage(1);
    }
  };

  const handleSearchKey = (e) => {
    if (e.key === "Enter") {
        handleApplyFilters();
    }
  };

  const ChildTable = ({ rows }) => {
    if (!rows?.length) return <p className={styles.emptyChildMsg}>No Response found.</p>;

    const childColumns = [
      { key: "user_id", label: "USER ID" },
      {
        key: "response_date",
        label: "RESPONSE DATE & TIME",
        render: (_, row) => {
          if (!row.response_date && !row.response_time) return "-";
          return formatISTDateTimeFromParts(row.response_date, row.response_time);
        },
      },
      { key: "response_text", label: "RESPONSE TEXT" },
      { key: "responded_by", label: "RESPONDED BY" },
      {
        key: "closed_date",
        label: "CLOSED DATE & TIME",
        render: (_, row) => {
          if (!row.closed_date && !row.closed_time) return "-";
          return formatISTDateTimeFromParts(row.closed_date, row.closed_time);
        },
      },
      { key: "complaint_status", label: "COMPLAINT STATUS" },
      { key: "closed_time", label: "CLOSED TIME" },
      { key: "clousure_text", label: "CLOSURE TEXT" },
      { key: "closed_by", label: "CLOSED BY" },
      { key: "communication_track_id", label: "COMM TRACK ID" },
      {
        key: "attachment",
        label: "ATTACHMENT",
        render: (fileName) => {
          if (!fileName) return "No File";
          const url = `${REACT_APP_API_BASE_URL}uploads/attachments/${fileName}`;
          const downloadFile = () => {
            fetch(url, { method: "GET" })
              .then((res) => res.blob())
              .then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(blobUrl);
              })
              .catch(() => alert("Failed to download file"));
          };
          return (
            <button onClick={downloadFile} className={styles.downloadButton} title="Download Attachment">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3v12m0 0l-5-5m5 5 5-5M5 19h14" stroke="#000" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          );
        },
      }
    ];

    return (
      <table className={styles.childTable}>
        <thead>
          <tr>
            {childColumns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {childColumns.map((col) => (
                <td key={col.key}>
                  {col.render ? col.render(row[col.key], row) : row[col.key] || "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className={styles.pageContainer}>
      <ToastContainer position="top-center" autoClose={1500} />

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Ticket</h1>
          <p>Manage and track user Ticket and complaints</p>
        </div>
        
        {hasPermission("CREATE_GRIEVANCE") && (
            <button className={styles.btnAdd} onClick={() => setShowAddModal(true)}>
              + Add Ticket
            </button>
        )}
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.filtersLeft}>
            <div className={styles.filterGroup}>
                <label>From Date</label>
                <input type="date" className={styles.input} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className={styles.filterGroup}>
                <label>To Date</label>
                <input type="date" className={styles.input} value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className={styles.filterGroup}>
                <label>Status</label>
                <select className={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">Select Status</option>
                    {statusOptions.map(opt => <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>)}
                </select>
            </div>
            <div className={styles.filterGroup}>
                <label>Complaint Type</label>
                <select className={styles.select} value={complaintType} onChange={(e) => setComplaintType(e.target.value)}>
                    <option value="">Select Type</option>
                    {complaintTypeOptions.map(opt => <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>)}
                </select>
            </div>
        </div>
        <div className={styles.filtersRight}>
            <div className={styles.searchWrap}>
              <SearchIcon />
              <input
                type="text"
                placeholder="Search User ID, Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKey}
              />
            </div>
            <button className={styles.btnSearch} onClick={handleApplyFilters}>Search</button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <CollapsibleTable
            columns={columns}
            data={tableData}
            noScroll={true}
            rowStyle={getRowStyle}
            onRowExpand={(row) => <ChildTable rows={row.childData} />}
        />
      </div>

      <div className={styles.tableFooter}>
            <span>
                Showing page {page} of {totalPages}
            </span>

            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        type="button"
                        className={styles.pageBtn}
                        disabled={page === 1 || loading}
                        onClick={() => setPage(page - 1)}
                    >
                        {"<"}
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                        if (
                            p === 1 ||
                            p === totalPages ||
                            (p >= page - 1 && p <= page + 1)
                        ) {
                            return (
                                <button
                                    type="button"
                                    key={p}
                                    className={`${styles.pageBtn} ${page === p ? styles.activePage : ""}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            );
                        }

                        if (p === page - 2 || p === page + 2) {
                            return (
                                <span key={p} className={styles.pagerSeparator}>
                                    ...
                                </span>
                            );
                        }

                        return null;
                    })}

                    <button
                        type="button"
                        className={styles.pageBtn}
                        disabled={page === totalPages || loading}
                        onClick={() => setPage(page + 1)}
                    >
                        {">"}
                    </button>
                </div>
            )}

            <span className={styles.refreshTime}>
                Last refreshed: {formatISTDateTime(new Date())}
            </span>
        </div>

      {/* IMAGE PREVIEW */}
      {previewImage && (
        <div
          className={styles.imageOverlay}
          onClick={() => setPreviewImage(null)}
        >
          <img src={previewImage} alt="img" className={styles.previewImage} />
        </div>
      )}

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Ticket"
        size="large"
        closeOnOverlay={false}
      >
        <AddGrievances
          onSubmitted={() => {
            setShowAddModal(false);
            fetchGrievances({ ...appliedFilters, page, limit: 20 });
          }}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      <Modal
        isOpen={showResponseModal}
        onClose={() => setShowResponseModal(false)}
        title="Add Ticket Response"
        size="large"
        closeOnOverlay={false}
      >
        <AddGrievanceResponse
          complaintId={selectedComplaintId}
          onSubmitted={() => {
            setShowResponseModal(false);
            if (expandedRow === selectedComplaintId) {
                handleExpand({ complaint_id: selectedComplaintId }); // toggle off
                setTimeout(() => handleExpand({ complaint_id: selectedComplaintId }), 300); // toggle back on to refresh
            }
          }}
          onCancel={() => setShowResponseModal(false)}
        />
      </Modal>

      <LeadNotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        rawNotes={selectedNotes}
      />
    </div>
  );
}
